import React from 'react';
import Card from '../components/common/Card';

const BookingPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto py-12 px-4 space-y-8">
        {/* Header */}
        <section className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-emphasis mb-4">
            Registration, Booking, Commission &amp; Appointment Deposit Policy
          </h1>
          <p className="text-secondary">Salon Market — Connecting Clients with Trusted Beauty Professionals Across Uganda</p>
        </section>

        {/* Content */}
        <Card className="p-8">
          <div className="prose prose-sm max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">1. Purpose</h2>
              <p className="text-secondary mb-3">
                This Policy establishes the rules governing the registration of service providers, booking
                charges, appointment deposits, commission payments, and related matters on the Salon
                Marketplace. It is intended to ensure transparency, fairness, and efficient operation of the
                platform for all users.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">2. Service Provider Registration</h2>
              <p className="text-secondary mb-3">
                Every salon, barber, nail technician, makeup artist, hairstylist, tattooist, massage therapist,
                or other beauty service provider wishing to offer services through Salon Market shall register
                under one of the following categories:
              </p>
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--orange-2)' }}>
                      <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--gray-12)' }}>Registration Plan</th>
                      <th className="text-left px-4 py-2.5 font-semibold" style={{ color: 'var(--gray-12)' }}>Registration Fee (UGX)</th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary">
                    <tr className="border-t border-gray-200">
                      <td className="px-4 py-2.5">Ordinary Registration</td>
                      <td className="px-4 py-2.5">20,000</td>
                    </tr>
                    <tr className="border-t border-gray-200">
                      <td className="px-4 py-2.5">Advanced Registration</td>
                      <td className="px-4 py-2.5">30,000</td>
                    </tr>
                    <tr className="border-t border-gray-200">
                      <td className="px-4 py-2.5">Premium Registration</td>
                      <td className="px-4 py-2.5">80,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4 mb-3">
                <li>Registration fees are payable before a provider account is approved.</li>
                <li>Registration fees are non-refundable except where Salon Market determines that an error occurred during payment or registration.</li>
                <li>Salon Market reserves the right to approve, reject, suspend, or terminate any application that fails to meet the platform's standards or violates applicable laws or policies.</li>
              </ul>
              <h3 className="text-base font-bold text-emphasis mb-2">Future Review of Registration Fees</h3>
              <p className="text-secondary mb-3">
                As the platform grows and market conditions change, the Board of Directors or other duly
                authorised governing body of Salon Market may review, increase, reduce, or restructure the
                registration fees where necessary. Any such changes shall be communicated to existing and
                prospective service providers before taking effect.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">3. Booking Charges (Platform Commission)</h2>
              <p className="text-secondary mb-3">
                For every confirmed booking made through Salon Market, the Company shall retain a booking
                commission equal to <strong>Eight Percent (8%)</strong> of the total service value.
              </p>
              <p className="text-secondary mb-3">
                This commission is payable to the Company for providing the booking platform, customer
                support, payment facilitation, marketing, and other operational services. The commission
                shall be deducted before the remaining balance is paid to the service provider, where applicable.
              </p>
              <h3 className="text-base font-bold text-emphasis mb-2">Future Review of Booking Charges</h3>
              <p className="text-secondary mb-3">
                Salon Marketplace reserves the right to increase or reduce the booking commission based on
                business requirements, operational costs, market conditions, or decisions made by the Board
                of Directors. Reasonable notice of any changes shall be given to all registered service
                providers before the revised commission takes effect.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">4. Appointment Deposit Policy</h2>
              <p className="text-secondary mb-3">To secure appointments, customers shall pay the following deposits:</p>
              <h3 className="text-base font-bold text-emphasis mb-2">Regular Appointments</h3>
              <p className="text-secondary mb-3">
                A deposit of <strong>Thirty Percent (30%)</strong> of the total service cost shall be required.
              </p>
              <h3 className="text-base font-bold text-emphasis mb-2">Weekend, Holiday and Party Appointments</h3>
              <p className="text-secondary mb-3">
                Appointments scheduled on weekends, public holidays, special events, weddings, parties, or
                other high-demand periods shall require a deposit of <strong>Fifty Percent (50%)</strong> of
                the total service cost.
              </p>
              <p className="text-secondary mb-3">
                The deposit forms part of the total service price and shall be deducted from the customer's
                final bill upon successful completion of the appointment.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">5. Rescheduling</h2>
              <p className="text-secondary mb-3">
                A customer may reschedule an appointment <strong>once</strong> without losing the deposit,
                provided that at least <strong>twenty-four (24) hours' notice</strong> is given before the
                scheduled appointment time. Any additional rescheduling requests shall be subject to approval
                by the service provider and Salon Market.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">6. Cancellation and No-Show Policy</h2>
              <p className="text-secondary mb-3">
                Where a customer cancels on the same day as the appointment or fails to attend without prior
                notice, the appointment deposit shall be forfeited. The forfeited deposit shall not be
                refunded to the customer except where Salon Market determines that exceptional circumstances
                justify a refund.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">7. Commission on Forfeited Deposits</h2>
              <p className="text-secondary mb-3">
                Where a customer's deposit is forfeited due to a same-day cancellation or failure to attend
                the appointment, Salon Market shall retain its standard <strong>Eight Percent (8%)</strong>{' '}
                commission on the forfeited amount. The remaining balance of the forfeited deposit, after
                deduction of the Company's commission where applicable, shall be handled in accordance with
                the Company's financial procedures and any agreement with the service provider.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">8. Amendments</h2>
              <p className="text-secondary mb-3">
                Salon Marketplace reserves the right to amend, update, or revise this Policy at any time.
                Where material changes are made, registered users and service providers shall be notified
                through the website, email, SMS, or any other official communication channel before the
                amendments take effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-emphasis mb-3">9. Acceptance</h2>
              <p className="text-secondary">
                By registering on Salon Marketplace or making a booking through the platform, every service
                provider and customer confirms that they have read, understood, and agree to be bound by this
                Registration, Booking, Commission and Appointment Deposit Policy.
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BookingPolicy;
